/** Supported document categories for viewer dispatch. */
export type DocumentType = 'pdf' | 'image' | 'presentation' | 'document' | 'spreadsheet';

/** Metadata returned by the upload endpoint and used throughout the UI. */
export interface DocumentInfo {
  documentId: string;
  fileName: string;
  mimeType: string;
  documentType: DocumentType;
  /** Raw document bytes (PDF binary, image bytes, converted PDF for presentations, native DOCX, or native XLSX). */
  data: Uint8Array;
}

/**
 * Map a file NAME to a document category.
 *
 * Android reports an empty type or application/octet-stream for files picked
 * from Downloads or Drive, so getDocumentType alone returns null for a
 * perfectly ordinary PDF and the upload was refused before it ever left the
 * phone — Teams Android issue 11, where picking a file did nothing.
 *
 * Only a hint for whether to ATTEMPT the upload. The server re-derives the
 * type from the bytes and rejects anything the content does not support, so
 * a misleading name buys nothing here.
 */
export function getDocumentTypeFromName(fileName: string): DocumentType | null {
  const ext = fileName.includes('.') ? fileName.split('.').pop()!.toLowerCase() : '';
  switch (ext) {
    case 'pdf':
      return 'pdf';
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'webp':
    case 'bmp':
    case 'tif':
    case 'tiff':
    case 'svg':
      return 'image';
    case 'pptx':
    case 'ppt':
      return 'presentation';
    case 'docx':
    case 'doc':
      return 'document';
    case 'xlsx':
    case 'xls':
      return 'spreadsheet';
    default:
      return null;
  }
}

/** Map MIME types to document categories. */
export function getDocumentType(mimeType: string): DocumentType | null {
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType.startsWith('image/')) return 'image';
  if (
    mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
    mimeType === 'application/vnd.ms-powerpoint'
  ) {
    return 'presentation';
  }
  if (
    mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    mimeType === 'application/msword'
  ) {
    return 'document';
  }
  if (
    mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mimeType === 'application/vnd.ms-excel'
  ) {
    return 'spreadsheet';
  }
  return null;
}

/** All MIME types accepted for upload. */
export const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/svg+xml',
  'image/webp',
  'image/gif',
  'image/bmp',
  'image/tiff',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
] as const;

/** For the HTML file input accept attribute. Includes file extensions for Office formats. */
export const ACCEPT_STRING = [
  ...ACCEPTED_MIME_TYPES,
  '.pptx',
  '.ppt',
  '.docx',
  '.doc',
  '.xlsx',
  '.xls',
].join(',');

/**
 * True on a touch device, which is our proxy for "an Android WebView is going
 * to handle this file input".
 *
 * Deliberately a media query and not user-agent sniffing: Teams' own mobile
 * emulation sets touch points, so this is also testable from a desktop browser.
 */
export const IS_COARSE_POINTER =
  typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches === true;

/**
 * What to put in the file input's `accept`, or undefined for "do not filter".
 *
 * Android turns `accept` into intent MIME filters, and it does NOT understand
 * bare extensions like `.pptx`. Our list mixes both, which can leave the
 * chooser matching nothing — reported from a real phone on 2026-08-30 as
 * "Choisir un fichier does nothing", on BOTH the dropzone and the inline
 * button. A MIME-only list was tried once (bcaf360) and broke the picker too,
 * so the remaining move is to stop filtering on touch devices altogether.
 *
 * Nothing is lost by it: handleFileChange still rejects unsupported types by
 * MIME and by extension, and the server re-derives the type from the bytes and
 * refuses anything the content does not support. `accept` was only ever a
 * convenience for the desktop dialog, which keeps it.
 */
export const FILE_INPUT_ACCEPT: string | undefined = IS_COARSE_POINTER ? undefined : ACCEPT_STRING;
